import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, Loading } from './states';

interface MfaStatus {
  enabled: boolean;
  pending: boolean;
  recoveryCodesRemaining: number;
  enforced: boolean;
}

/**
 * Self-service MFA management for the signed-in user.
 *
 * Enrollment is two-phase: the server hands back a secret and provisioning URI, the
 * user adds it to an authenticator, then proves possession with a code before it goes
 * active — so a user can never lock themselves out with a secret they never scanned.
 * Recovery codes and the secret are shown once, here, and never again.
 */
export function MfaPanel(): JSX.Element {
  const t = useT();
  const status = useAsync(() => api.get<MfaStatus>('/auth/mfa'), []);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const enroll = (): Promise<void> =>
    run(async () => {
      const res = await api.post<{ secret: string; otpauthUri: string }>('/auth/mfa/enroll');
      setSecret(res.secret);
      setOtpauthUri(res.otpauthUri);
      setRecoveryCodes(null);
      status.reload();
    });

  const activate = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<{ recoveryCodes: string[] }>('/auth/mfa/activate', { code });
      setRecoveryCodes(res.recoveryCodes);
      setSecret(null);
      setOtpauthUri(null);
      setCode('');
      status.reload();
    });
  };

  const disable = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      await api.post('/auth/mfa/disable', { password });
      setPassword('');
      setRecoveryCodes(null);
      status.reload();
    });
  };

  const regenerate = (e: React.FormEvent): void => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<{ recoveryCodes: string[] }>('/auth/mfa/recovery-codes', {
        password,
      });
      setRecoveryCodes(res.recoveryCodes);
      setPassword('');
      status.reload();
    });
  };

  if (status.loading) return <Loading />;
  const s = status.data;

  return (
    <div className="card">
      <h2>{t('mfa.title')}</h2>
      {error ? <Banner variant="error">{error}</Banner> : null}

      {recoveryCodes ? (
        <Banner variant="warning">
          <div>{t('mfa.recovery.saveNow')}</div>
          <pre className="code-block">{recoveryCodes.join('\n')}</pre>
        </Banner>
      ) : null}

      {s?.enabled ? (
        <>
          <p>
            <span className="chip chip--success">{t('mfa.on')}</span>{' '}
            {t('mfa.recovery.remaining').replace('{count}', String(s.recoveryCodesRemaining))}
          </p>
          <form className="form form--inline" onSubmit={regenerate}>
            <label className="field">
              <span>{t('mfa.password')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="btn">
              {t('mfa.recovery.regenerate')}
            </button>
            {!s.enforced ? (
              <button type="button" className="btn btn--ghost btn--danger" onClick={disable}>
                {t('mfa.disable')}
              </button>
            ) : (
              <span className="chip">{t('mfa.enforced')}</span>
            )}
          </form>
        </>
      ) : secret ? (
        <>
          <p>{t('mfa.enroll.scan')}</p>
          <p>
            {t('mfa.enroll.secret')}: <code>{secret}</code>
          </p>
          {otpauthUri ? (
            <p className="code-block" style={{ wordBreak: 'break-all' }}>
              {otpauthUri}
            </p>
          ) : null}
          <form className="form form--inline" onSubmit={activate}>
            <label className="field">
              <span>{t('mfa.enroll.enterCode')}</span>
              <input
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="btn btn--primary">
              {t('mfa.enroll.activate')}
            </button>
          </form>
        </>
      ) : (
        <>
          <p>
            <span className="chip">{t('mfa.off')}</span>
            {s?.enforced ? ` · ${t('mfa.setupRequired')}` : ''}
          </p>
          <button type="button" className="btn btn--primary" onClick={() => void enroll()}>
            {t('mfa.enroll.start')}
          </button>
        </>
      )}
    </div>
  );
}
